import type { ReactNode } from "react";
import { Plus } from "lucide-react";
import type { Varianta, ZakazkaDetail } from "@/api/schemas";
import { formatEur } from "@/lib/format";
import { jeVCene, type OdborZakazky } from "@/lib/odboryZakazky";
import { cn } from "@/lib/utils";

const STITOK: Record<OdborZakazky, string> = {
  nabytok: "Výpočty",
  tovar: "Rozpočty tovaru",
  vyroba: "Výroba",
};

const NOVA: Record<OdborZakazky, { text: string; title: string }> = {
  nabytok: { text: "Výpočet", title: "Nový výpočet — ďalšia alternatíva ceny pre zákazníka" },
  tovar: {
    text: "Tovar",
    title: "Nový rozpočet tovaru (regály CRC) — cena sa pripočíta k nábytku, ponuka je jedna",
  },
  vyroba: {
    text: "Časť",
    title: "Ďalšia časť výroby — na pílu ide samostatne, výroba spolu je súčet častí",
  },
};

/** Popis verzie do tooltipu — čo verzia v odbore znamená. */
function popisVerzie(v: Varianta): string {
  if (v.odbor === "tovar") return "Tovar — regály CRC";
  if (v.je_cast) return "Časť výroby — na pílu ide samostatne, výroba spolu je súčet častí";
  if (v.je_vyrobna) return "Výroba — realita výroby (výrobná kalkulácia)";
  return v.popis ?? "Výpočet — cena pre zákazníka";
}

/**
 * Verzie vybraného odboru pod lištou odborov (KOLO 240, návrh H1b) —
 * kapsule s menom, **★ = v cene zákazky** a cenou s DPH. Vybraná má ⋮
 * s akciami verzie (Premenovať, Dať do ceny, Aktualizovať z exportu,
 * Zmazať); na konci „+ Výpočet / + Tovar / + Časť".
 *
 * Jeden výber — `KapsuleVariant` (Objednávky, Cut Rite) je viacvýber.
 */
export function KapsuleVerzii({
  zakazka,
  odbor,
  verzie,
  aktivnaId,
  onVyber,
  onNova,
  menu,
}: {
  zakazka: Pick<ZakazkaDetail, "aktivna_varianta_id" | "aktivna_tovar_varianta_id" | "cena_z_vyroby">;
  odbor: OdborZakazky;
  verzie: Varianta[];
  aktivnaId: string | null;
  onVyber: (id: string) => void;
  /** Nová verzia v odbore — bez práva chýba. */
  onNova?: () => void;
  /** ⋮ akcie vybranej verzie. */
  menu?: ReactNode;
}): JSX.Element {
  return (
    <div
      className="flex min-w-0 items-center gap-2 overflow-x-auto"
      role="tablist"
      aria-label={STITOK[odbor]}
    >
      <span className="shrink-0 text-[10px] font-extrabold uppercase tracking-wider text-muted-foreground">
        {STITOK[odbor]}
      </span>
      {verzie.map((v) => {
        const akt = v.id === aktivnaId;
        const vCene = jeVCene(zakazka, v);
        return (
          <div
            key={v.id}
            className={cn(
              "flex h-7 shrink-0 items-center rounded-full border font-ui text-[13px] font-semibold whitespace-nowrap",
              akt
                ? "border-[#f6c4c6] bg-tandem-red-light text-tandem-red-dark"
                : "bg-background text-foreground hover:bg-muted",
            )}
          >
            <button
              type="button"
              role="tab"
              aria-selected={akt}
              onClick={() => onVyber(v.id)}
              className={cn("flex h-full items-center gap-1.5 rounded-full pl-3", akt && menu ? "pr-1" : "pr-3")}
              title={`${popisVerzie(v)}${vCene ? " · v cene zákazky" : ""}`}
            >
              {v.nazov}
              {odbor === "nabytok" && v.popis ? (
                <span className="max-w-[140px] truncate font-medium text-muted-foreground">{v.popis}</span>
              ) : null}
              {vCene ? (
                <span className="text-amber-500" aria-label="v cene zákazky">
                  ★
                </span>
              ) : null}
              <span
                className={cn("font-medium tabular-nums", akt ? "text-tandem-red-dark" : "text-muted-foreground")}
              >
                {formatEur(v.cena_s_dph)}
              </span>
            </button>
            {akt && menu ? <span className="pr-0.5">{menu}</span> : null}
          </div>
        );
      })}
      {onNova ? (
        <button
          type="button"
          onClick={onNova}
          className="flex h-7 shrink-0 items-center gap-1 rounded-full px-2.5 font-ui text-[13px] font-semibold text-muted-foreground hover:bg-muted hover:text-foreground"
          title={NOVA[odbor].title}
        >
          <Plus className="h-3.5 w-3.5" aria-hidden />
          {NOVA[odbor].text}
        </button>
      ) : null}
    </div>
  );
}
